import { Building2, Check, ChevronDown, ChevronsUpDown, Lock, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/atoms/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/molecules/tooltip";
import { useWorkspace } from "@/hooks/use-workspace";
import { cn } from "@/lib/utils";

function isPrivate(name: string): boolean {
  return name === "Private";
}

type WorkspaceSwitcherProps = {
  /** "sidebar" renders a full-width row that opens upwards, for the sidebar footer. */
  placement?: "header" | "sidebar";
};

export function WorkspaceSwitcher({ placement = "header" }: WorkspaceSwitcherProps) {
  const inSidebar = placement === "sidebar";
  const { workspaces, activeWorkspace, isLoading, switchWorkspace } = useWorkspace();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="h-4 w-20 rounded bg-muted animate-pulse" />
      </div>
    );
  }

  const displayName = activeWorkspace?.name ?? "Private";

  return (
    <div ref={ref} className="relative">
      {inSidebar ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="hover:bg-sidebar-accent flex h-10 w-full items-center gap-2.5 px-1.5 text-start text-[13px] transition-colors"
        >
          <span className="bg-highlight text-highlight-foreground flex size-6 shrink-0 items-center justify-center text-xs font-medium">
            {displayName.slice(0, 1).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1 truncate">{displayName}</span>
          <ChevronsUpDown className="text-muted-foreground size-3.5 shrink-0" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium hover:bg-accent transition-colors"
        >
          <Building2 className="size-3.5 text-muted-foreground shrink-0" />
          <span className="truncate max-w-[100px]">{displayName}</span>
          <ChevronDown className="size-3 text-muted-foreground shrink-0" />
        </button>
      )}

      {open && (
        <TooltipProvider delayDuration={300}>
          <div
            className={cn(
              "absolute left-0 z-50 w-56 rounded-lg border bg-popover py-1 shadow-md",
              inSidebar ? "bottom-full mb-1" : "top-full mt-1",
            )}
          >
            <div className="px-2 py-1.5">
              <p className="text-xs font-medium text-muted-foreground">Workspaces</p>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {workspaces.map((ws) => (
                <button
                  key={ws.id}
                  type="button"
                  onClick={() => {
                    switchWorkspace(ws.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left"
                >
                  <Avatar size="sm">
                    <AvatarImage src={ws.logo ?? undefined} alt={ws.name} />
                    <AvatarFallback>{ws.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate">{ws.name}</span>
                  {isPrivate(ws.name) ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="shrink-0 text-muted-foreground">
                          <Lock className="size-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        Personal workspace. Only you can access it.
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    (ws.role === "owner" || ws.role === "admin") && (
                      <span className="text-[10px] text-muted-foreground capitalize bg-muted px-1 py-0.5 rounded shrink-0">
                        {ws.role}
                      </span>
                    )
                  )}
                  {ws.id === activeWorkspace?.id && (
                    <Check className="size-3.5 text-primary shrink-0" />
                  )}
                </button>
              ))}
            </div>
            <div className="border-t px-1 py-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  const event = new CustomEvent("open-settings-workspaces");
                  window.dispatchEvent(event);
                }}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              >
                <Plus className="size-3.5" />
                Create new workspace
              </button>
            </div>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}
